import { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Phone, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function VehicleServiceHero({ 
  brand = 'BMW',
  brandColor = '#ffffffffff',
  tagline = 'Expert Repair & Service',
  description,
  features = [],
  heroImage = null, // No default - only show image container if explicitly provided
  heroImageAlt = null,
  heroVariant = 'default'
}) {
  const heroRef = useRef(null);
  const imageRef = useRef(null);
  const glowRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 992);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // GSAP Effects
  useEffect(() => {
    if (!imageRef.current) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: heroRef.current,
        start: "top top",
        end: "bottom top",
        scrub: 1,
      }
    });

    // Parallax and 3D rotation effect
    tl.to(imageRef.current, {
      y: 100,
      rotationY: 10,
      rotationX: -5,
      scale: 1.1,
      duration: 1
    });

    // Glow pulse effect
    if (glowRef.current) {
      gsap.to(glowRef.current, {
        opacity: 0.8,
        scale: 1.2,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut"
      });
    }

    // Mouse move effect
    const handleMouseMove = (e) => {
      if (!imageRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const xPos = (clientX / innerWidth - 0.5) * 20;
      const yPos = (clientY / innerHeight - 0.5) * 20;
      
      gsap.to(imageRef.current, {
        rotationY: xPos,
        rotationX: -yPos,
        duration: 0.5,
        ease: "power2.out"
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Parallax effect based on scroll through the hero section
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start end', 'end start'] });
  const amplitude = isMobile ? 30 : (heroVariant === 'jaguar' ? 200 : 60);
  const scaleAmp = isMobile ? [1.01, 0.99] : (heroVariant === 'jaguar' ? [1.08, 0.94] : [1.02, 0.98]);
  const yParallax = useTransform(scrollYProgress, [0, 1], [0, -amplitude]);
  const scaleParallax = useTransform(scrollYProgress, [0, .8], scaleAmp);
  const opacityParallax = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [1, 1, 0.8, 0.6]);

  const revealVariants = {
    visible: (i) => ({
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.4,
        duration: 0.5,
      },
    }),
    hidden: {
      filter: "blur(10px)",
      y: -20,
      opacity: 0,
    },
  };

  const svgVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1.5,
        ease: "easeOut",
      },
    },
  };

  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        duration: 2,
        ease: "easeInOut",
      },
    },
  };

  // Split text animation for title
  const titleWords = `${tagline} for ${brand}`.split(' ');

  return (
    <section className="bg-gradient-to-b from-black via-gray-800 to-black relative overflow-hidden min-h-screen" ref={heroRef}>
      {/* Enhanced background with grid and gradient */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(#ffffff11_1px,transparent_1px)] bg-[size:20px_20px] opacity-90"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent"></div>
      </div>
   
    
      {/* Main Hero Content - removed duplicate navigation */}
      <article className="text-white py-32 pt-40 w-fit max-w-4xl mx-auto text-center space-y-4 relative z-10 px-4">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl capitalize font-medium leading-[120%]">
          <motion.div className="flex flex-wrap justify-center">
            {titleWords.map((word, i) => (
              <motion.span
                key={i}
                initial={{ y: 50, opacity: 0, rotateX: -90 }}
                animate={{ y: 0, opacity: 1, rotateX: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 250,
                  damping: 40,
                  delay: 0.3 + i * 0.15
                }}
                className="inline-block mr-4"
                style={{ color: word === brand ? brandColor : 'white' }}
              >
                {word}{i < titleWords.length - 1 ? ' ' : ''}
              </motion.span>
            ))}
          </motion.div>
        </h1>

        <motion.p
          initial="hidden"
          animate="visible"
          custom={1}
          variants={revealVariants}
          className="max-w-xl mx-auto text-sm sm:text-base text-gray-300"
        >
          {description || `Factory-trained technicians specializing in ${brand} vehicles. Save up to 40% compared to dealership prices with the same quality service.`}
        </motion.p>

        <motion.div
          initial="hidden"
          animate="visible"
          custom={2}
          variants={revealVariants}
          className="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-8 sm:mt-10 mx-auto w-fit"
        >
          <Link 
            to="/contact"
            className="text-lg h-12 px-6 rounded-lg text-white flex items-center justify-center gap-2 bg-green-600 relative before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-2 before:bg-gradient-to-t before:from-green-600 before:to-green-300 before:rounded-t-lg transition-all group"
          >
            <Calendar className="w-5 h-5" />
            Schedule Your {brand} Service
          </Link>
          <a 
            href="tel:8173822316"
            className="text-lg h-12 px-6 text-white flex items-center justify-center gap-2 rounded-lg group border border-white/20 hover:bg-white/10"
          >
            <Phone className="w-5 h-5" />
            Call (817) 382-2316
          </a>
        </motion.div>

        {/* Feature pills */}
        {features.length > 0 && (
          <motion.div
            initial="hidden"
            animate="visible"
            custom={3}
            variants={revealVariants}
            className="flex flex-wrap gap-3 justify-center mt-12"
          >
            {features.map((feature, i) => (
              <span 
                key={i}
                className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-sm"
              >
                {feature}
              </span>
            ))}
          </motion.div>
        )}
      </article>

      {/* Bottom decorative element */}
    
        {heroVariant === 'jaguar' && (
          <motion.svg
            width="1288"
            height="162"
            viewBox="0 0 1288 162"
            className="absolute -top-16 left-0 w-full z-10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.8, duration: 1 }}
          >
            <g style={{ mixBlendMode: 'plus-lighter' }} filter="url(#filter0_f_jaguar_arc)">
              <motion.path
                d="M1223 95.5007C1082 95.5007 920.76 94.5 649.5 94.5C378.239 94.5 182.5 96.9996 65 96.9996C217 75.5 362.793 65 638.646 65C914.498 65 1078.5 75 1223 95.5007Z"
                fill="url(#paint0_linear_jaguar_arc)"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1, opacity: [0.4, 0.9, 0.6, 0.9] }}
                transition={{ duration: 1.8, delay: 1.8 }}
              />
            </g>
            <defs>
              <filter id="filter0_f_jaguar_arc" x="0.6" y="0.6" width="1286.8" height="160.8" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feGaussianBlur stdDeviation="32.2" />
              </filter>
              <linearGradient id="paint0_linear_jaguar_arc" x1="641.493" y1="65" x2="641.493" y2="96.9996" gradientUnits="userSpaceOnUse">
                <stop stopColor={brandColor} />
                <stop offset="1" stopColor={brandColor} />
              </linearGradient>
            </defs>
          </motion.svg>
        )}

      {/* Hero Image - BADASS CAR WITH EFFECTS - Only render if heroImage is provided */}
      {heroImage && (
        <motion.div 
          className="absolute bottom-0 left-0 right-0 mx-auto w-[85%] max-w-7xl z-[5] pointer-events-none"
          initial={{ opacity: 0, y: 100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
        >
      
            {/* Main image container with 3D transform */}
            <div 
              ref={imageRef}
              className="relative overflow-hidden"
              style={{
                transformStyle: 'preserve-3d',
                transform: 'translateZ(0)',
              }}
            >
       
              {/* Scanline effect */}
              <div className="absolute inset-0 z-20 pointer-events-none opacity-80"
                style={{
                  backgroundImage: `repeating-linear-gradient(
                    0deg,
                    transparent,
                    transparent 2px,
                    rgba(255, 255, 255, 1)) 2px,
                    rgba(255, 255, 255, 0.5) 4px
                  )`,
                  animation: 'scanlines 8s linear infinite',
                }}
              />

              {/* The actual car image */}
              <motion.img
                src={heroImage}
                alt={`${brand} Performance Vehicle`}
                className="w-full xl:pt-36 lg:pt-24 md:pt-24 sm:pt-4 h-auto z-0 object-contain"
                style={{
                  aspectRatio: '21/9',
                  objectPosition: 'center 60%',
                }}
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                transition={{ duration: 2, ease: "easeOut" }}
              />

              {/* Reflection effect */}
              <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              
              {/* Speed lines effect */}
              <div className="absolute inset-y-0 left-0 w-full opacity-0"
                style={{
                  backgroundImage: `linear-gradient(90deg, 
                    transparent 0%, 
                    rgba(255, 255, 255, 0.1) 45%, 
                    rgba(255, 255, 255, 0.3) 50%, 
                    rgba(255, 255, 255, 0.1) 55%, 
                    transparent 100%)`,
                  backgroundSize: '200% 100%',
                  animation: 'speed-lines 2s linear infinite',
                }}
              />
            </div>

            {/* Floating particles */}
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full opacity-0"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animation: `float-particle ${5 + Math.random() * 10}s linear infinite`,
                    animationDelay: `${Math.random() * 5}s`,
                  }}
                />
              ))}
            </div>
        </motion.div>
      )}

      {/* Add CSS animations */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes gradient-rotate {
          0% { filter: hue-rotate(0deg) blur(1px); }
          100% { filter: hue-rotate(360deg) blur(1px); }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes scanlines {
          0% { transform: translateY(0); }
          100% { transform: translateY(10px); }
        }
        @keyframes speed-lines {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes float-particle {
          0% { transform: translate(0, 0) scale(0); opacity: 0; }
          10% { transform: translate(10px, -10px) scale(1); opacity: 0; }
          90% { transform: translate(-10px, -100px) scale(1); opacity: 0; }
          100% { transform: translate(-20px, -120px) scale(0); opacity: 0; }
        }
        @keyframes float {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(30px, -30px); }
          50% { transform: translate(-20px, 20px); }
          75% { transform: translate(40px, 10px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.2; transform: scale(1.05); }
        }
      `}} />
    </section>
  );
}

export default VehicleServiceHero;
